<script setup>
import { watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['close'])

const { t } = useI18n()

const handleClose = () => {
  emit('close')
}

const handleEscape = (e) => {
  if (e.key === 'Escape' && props.isOpen) {
    handleClose()
  }
}

const handleBackdropClick = (e) => {
  if (e.target === e.currentTarget) {
    handleClose()
  }
}

// Prevent body scroll when modal is open
watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

onMounted(() => {
  window.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleEscape)
  document.body.style.overflow = ''
})
</script>

<template>
  <Transition
    enter-active-class="transition-opacity duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="isOpen"
      @click="handleBackdropClick"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="t('leadForm.error.title')"
    >
      <Transition
        enter-active-class="transition-all duration-300"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition-all duration-200"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="isOpen"
          class="bg-theme-primary rounded-2xl shadow-2xl max-w-md w-full p-6 border border-theme-default"
        >
          <!-- Header -->
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-bold text-theme-primary">
              {{ t('leadForm.error.title') }}
            </h2>
            <button
              @click="handleClose"
              class="text-theme-tertiary cursor-pointer transition-colors"
              :aria-label="t('leadForm.error.close')"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Error Icon -->
          <div class="flex justify-center mb-4">
            <div class="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <svg class="w-8 h-8 text-theme-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>

          <!-- Error Message -->
          <div class="text-center mb-6">
            <p class="text-theme-secondary">
              {{ errorMessage || t('leadForm.error.genericMessage') }}
            </p>
          </div>

          <!-- Actions -->
          <div class="flex gap-3">
            <button
              @click="handleClose"
              class="flex-1 px-4 py-3 button-primary font-medium rounded-lg transition-colors cursor-pointer"
            >
              {{ t('leadForm.error.close') }}
            </button>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<style scoped>
.bg-theme-primary {
  background-color: var(--color-theme-bg-primary);
}
.bg-theme-primary-90{
  background-color: color-mix(in oklab, var(--color-theme-bg-primary) 90%, transparent);
}
.button-primary{
  background-color: var(--theme-button-primary-bg);
  color: var(--color-theme-text-inverted);
  border-color: var(--theme-button-primary-border);
}
.button-primary:hover{
  background-color: var(--theme-button-primary-bg-hover);
}

.text-theme-primary{
  color: var(--color-theme-primary-text);
}

.text-theme-secondary{
  color: var(--color-theme-secondary-text);
}
.text-theme-tertiary{
  color: var(--color-theme-text-tertiary);
}

.text-theme-error{
  color: var(--theme-form-error-text);
}
.border-theme-default{
  border-color: var(--color-theme-border);
}
</style>